import dynamic from 'next/dynamic';
import React, { Fragment, Node, useMemo } from 'react';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { SearchBanners } from 'Components/widgets/facet-search-results/search-banners/SearchBanners';
import { SearchResultsPlaceholder } from 'Components/widgets/facet-search-results/search-results-placeholder/SearchResultsPlaceholder';
import type { Props as ProductsGridProps } from 'Components/widgets/products-grid/ProductsGrid';
import { ProductsGrid } from 'Components/widgets/products-grid/ProductsGrid';
import { t } from 'Src/i18n';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import 'Store/trip-information/kids/kids.reducer';
import { MAX_NUMBER_OF_SELECTED_PRODUCTS } from 'Utils/faceting/constants';
import { SearchCompareLarge } from 'Components/widgets/facet-search-results/search-compare/large/SearchCompareLarge';
import { SearchCompareNarrow } from 'Components/widgets/facet-search-results/search-compare/narrow/SearchCompareNarrow';
import { SearchCompareButton } from 'Components/widgets/facet-search-results/search-compare-button/SearchCompareButton';
import { SearchWorldMap } from 'Components/widgets/facet-search-results/search-world-map/SearchWorldMap';
import Portal from 'Components/frameworks/portal/Portal';
const ProductsGridVirtualized = dynamic(
  () =>
    import('Components/widgets/products-grid-virtualized/ProductsGridVirtualized').then(
      (c) => c.ProductsGridVirtualized,
    ),
  {
    ssr: false,
  },
);
export type Props = {
  childcareActiveFilters: Array<string>;
  childcareAvailabilities: Array<any>;
  className?: string;
  comparisonUrl: string;
  displayPlaceholder: boolean;
  flexibility?: number | null | undefined;
  header: Node;
  config: any;
  offer?: Offer;
  isProductComparisonModeAuthorized?: boolean;
  isProductComparisonSelectionModeEnabled: boolean;
  isProductsBottomInViewport?: boolean;
  isProductsTopInViewport?: boolean;
  onClickCompareButton: () => void;
  orderByPrice?: 'asc' | 'desc';
  toggleProductComparisonSelectionMode: () => void;
  hasDepartureCity?: boolean;
  hasDepartureDate?: boolean;
  products: Array<any>;
  productsSelectedForComparison?: Array<string>;
  toggleProductSelectedForComparison?: (arg0: string) => unknown;
  isDesktop?: boolean;
  kids?: Array<Kids>;
  hasSeeMore?: boolean;
  seeMore?: ProductsGridProps['seeMore'];
  virtualizedGrid?: boolean;
  isSynchronousGrid?: boolean;
  withWorldMap?: boolean;
};
export const SearchResults = ({
  childcareActiveFilters = [],
  childcareAvailabilities = [],
  className,
  comparisonUrl,
  config = {},
  displayPlaceholder,
  flexibility,
  hasDepartureCity,
  hasDepartureDate,
  hasSeeMore,
  header,
  isDesktop = true,
  isProductComparisonModeAuthorized,
  isProductComparisonSelectionModeEnabled = false,
  isProductsBottomInViewport,
  isProductsTopInViewport,
  isSynchronousGrid = false,
  kids = [],
  offer,
  onClickCompareButton,
  orderByPrice,
  products = [],
  productsSelectedForComparison = [],
  seeMore,
  toggleProductComparisonSelectionMode,
  toggleProductSelectedForComparison,
  virtualizedGrid,
  withWorldMap = false,
}: Props) => {
  const withCallToAction = (): boolean => Boolean(config?.common?.editorial?.search?.seeMoreText);

  let display;

  if (hasSeeMore) {
    if (isSynchronousGrid) {
      display = {
        mobile: 1,
        phablet: 2,
        tablet: 3,
        desktop: 3,
      };
    } else {
      display = {
        mobile: 2,
        phablet: 4,
        tablet: 6,
        desktop: 6,
      };
    }
  }

  const hasChildcareAvailabilities = childcareAvailabilities.length > 0;
  const childcareIncluded = kids.some((kid) => kid.childcareIncluded);
  const orderByChildcareAvailability = Boolean(
    hasChildcareAvailabilities && (childcareIncluded || childcareActiveFilters.length > 0),
  );
  const childrenClassName = `${flexibility ? 'pt-20' : ''} ${isDesktop ? 'mb-20' : ''}`;
  const searchCompareProps = {
    buttonLabel: t('faceting.compare.buttonLabel'),
    cancelLabel: t('faceting.compare.cancelLabel'),
    comparisonToggle: toggleProductComparisonSelectionMode,
    comparisonClick: onClickCompareButton,
    comparisonUrl: comparisonUrl,
    collapseLabel: t('faceting.compare.collapseLabel'),
    dropzoneLabel: t('faceting.compare.dropzoneLabel'),
    expandLabel: t('faceting.compare.expandLabel'),
    isComparisonActive: isProductComparisonSelectionModeEnabled,
    isComparisonSubmitable:
      productsSelectedForComparison.length > 0 && hasDepartureDate && hasDepartureCity,
    isProductsBottomInViewport: isProductsBottomInViewport,
    isProductsTopInViewport: isProductsTopInViewport,
    mandatoryMessage:
      !hasDepartureDate || !hasDepartureCity ? t('faceting.compare.dateAndTransportMandatory') : '',
    resultsCount: products.length,
    selectedItems: products
      .filter((product) => productsSelectedForComparison.includes(product.id))
      .map((product) => ({
        header: product.header,
        id: product.id,
        order: productsSelectedForComparison.findIndex((p) => p === product.id),
        picture: product?.thumbnail?.pictures?.[0],
        price: product?.flag?.price,
        priceInfo: product?.flag?.priceInfo,
        url: product.url,
        offer,
      }))
      .sort((a, b) => a.order - b.order),
    selectedItemsMax: MAX_NUMBER_OF_SELECTED_PRODUCTS,
    toggleProductSelectedForComparison: toggleProductSelectedForComparison,
  };
  const searchWorldMapProps = useMemo(() => {
    if (withWorldMap) {
      // Sort products for SearchWorldMap to keep Resorts only
      const onlyResortProductList = products.filter(
        (product) => !product.isCruise && !product.isTour,
      );
      // Sort products for SearchWorldMap to keep only winter or summer resort if resorts have same coordinates
      const onlyUniqResortProductList = onlyResortProductList.filter(
        (resort, index, arr) =>
          arr.findIndex(({ coordinates }) =>
            ['longitude', 'latitude'].every((value) => {
              if (coordinates && resort.coordinates) {
                return coordinates[value] === resort.coordinates[value];
              }
            }),
          ) === index,
      );
      return {
        onlyUniqResortProductList,
        offer,
      };
    }
  }, [products.length]);
  return (
    <Fragment>
      {isProductComparisonModeAuthorized && isDesktop ? (
        <SearchCompareLarge {...searchCompareProps}>{header}</SearchCompareLarge>
      ) : (
        <SiteContainer size="none" className="py-10">
          {header}
        </SiteContainer>
      )}
      <SearchBanners position="top" />

      <SiteContainer size="none" className="border-lightGrey pt-20 lg:border-t-0">
        {withWorldMap && <SearchWorldMap {...searchWorldMapProps} />}
        {displayPlaceholder && <SearchResultsPlaceholder nbItems={8} />}
        {!displayPlaceholder &&
        virtualizedGrid &&
        !hasChildcareAvailabilities &&
        !withCallToAction() ? (
          <ProductsGridVirtualized
            childrenClassName={childrenClassName}
            className={className}
            products={products}
            onComparisonClick={toggleProductSelectedForComparison}
          />
        ) : (
          <ProductsGrid
            childrenClassName={childrenClassName}
            className={className}
            display={display}
            hasSeeMore={hasSeeMore}
            isSynchronousGrid={isSynchronousGrid}
            offer={offer}
            onComparisonClick={toggleProductSelectedForComparison}
            orderByChildcareAvailability={orderByChildcareAvailability}
            orderByPrice={orderByPrice}
            products={products}
            seeMore={seeMore}
            totalNumberToDisplay={products.length}
          />
        )}
      </SiteContainer>
      <div id="SearchCompareBeacon" />
      {!isDesktop && (
        <Fragment>
          <Portal name="clubmed-search-comparePortal">
            {isProductComparisonModeAuthorized && <SearchCompareNarrow {...searchCompareProps} />}
          </Portal>
          <Portal name="clubmed-search-compareButtonPortal">
            {searchCompareProps.isComparisonActive && (
              <button
                className="text-14 font-bold"
                onClick={() => {
                  searchCompareProps.comparisonToggle();
                }}
                type="button"
              >
                {searchCompareProps.cancelLabel}
              </button>
            )}
            <SearchCompareButton
              className="w-full text-center"
              comparisonToggle={searchCompareProps.comparisonToggle}
              comparisonClick={searchCompareProps.comparisonClick}
              comparisonUrl={searchCompareProps.comparisonUrl}
              isComparisonActive={searchCompareProps.isComparisonActive}
              isComparisonDisabled={Boolean(!products.length || products.length === 0)}
              isComparisonSubmitable={searchCompareProps.isComparisonSubmitable}
              label={searchCompareProps.buttonLabel}
              mandatoryMessage={searchCompareProps.mandatoryMessage}
              selectedItemsCount={searchCompareProps.selectedItems.length}
              selectedItemsMax={searchCompareProps.selectedItemsMax}
            />
          </Portal>
        </Fragment>
      )}
      <SearchBanners position="terms" />
    </Fragment>
  );
};
